import type React from "react";
import {useCallback, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router";
import {observer} from "mobx-react-lite";
import {Alert, Skeleton, Stack, Typography} from "@mui/material";
import {useStores} from "~/stores";
import {routes} from "~/routing/routes";
import {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import {ApprovalStatusEnum} from "~/services/classes/AiClasses";
import {listTaxonomyChanges} from "~/pages/taxonomy-builder-v2/services/taxonomyChanges";
import {YourChangesTable} from "~/pages/taxonomy-builder-v2/components/YourChanges/YourChangesTable";
import {
    useTaxonomyApprovalRequestQuery,
    useTaxonomyApprovalStateQuery,
} from "./hooks/useTaxonomyApprovalQueries";
import {useTaxonomyApprovalState} from "./hooks/useTaxonomyApprovalState";
import {type FinishTaxonomyApprovalVariables, useFinishTaxonomyApprovalMutation} from "./hooks/useFinishTaxonomyApprovalMutation";
import {canRevert, revertChange, revertOperation, setApproverNote} from "./services/approvalTreeEdits";
import {getApprovalReviewColumns} from "./components/ApprovalReviewColumns";
import {ApprovalReviewHeader} from "./components/ApprovalReviewHeader";
import {ApprovalReviewActionBar} from "./components/ApprovalReviewActionBar";
import {FinishReviewDialog} from "./components/FinishReviewDialog";

function indexById(root: m_taxonomy.Tree): Map<number, m_taxonomy.Tree> {
    const map = new Map<number, m_taxonomy.Tree>();
    const visit = (n: m_taxonomy.Tree) => {
        map.set(n.id, n);
        n.children.forEach(visit);
    };
    visit(root);
    return map;
}

const countReverted = (n: m_taxonomy.Tree): number =>
    (n.values.changes?.filter(c => c.reverted).length ?? 0) + n.children.reduce((sum, c) => sum + countReverted(c), 0);

/**
 * Review of a taxonomy approval request (`/approval/taxonomy/:approvalId`).
 * The requested changes are listed like the builder's "Your changes", from the approver's own copy of the
 * tree. The approver reverts the changes they don't accept, leaves a note per category (saved when the
 * field is left), can undo / redo those, and finishes by approving the rest or rejecting the request.
 * Only a pending request can be changed.
 */
export const TaxonomyApprovalPage: React.FC = observer(() => {
    const navigate = useNavigate();
    const {authStore} = useStores();
    const approvalId = Number(useParams().approvalId);

    const {data: request, isError: requestError} = useTaxonomyApprovalRequestQuery(approvalId);
    const {data: approvalState, isLoading, isError: stateError} = useTaxonomyApprovalStateQuery(approvalId);
    const history = useTaxonomyApprovalState(approvalId, approvalState);
    const finish = useFinishTaxonomyApprovalMutation(approvalId);
    const [decision, setDecision] = useState<FinishTaxonomyApprovalVariables['decision'] | null>(null);

    const tree = approvalState?.active?.state;
    const rows = useMemo(() => tree ? listTaxonomyChanges(tree) : [], [tree]);
    const byId = useMemo(() => tree ? indexById(tree) : new Map<number, m_taxonomy.Tree>(), [tree]);
    const readOnly = !request || !ApprovalStatusEnum.statusIsOpenForApprover(request.current_status.status);
    const isSaving = history.isPending || finish.isPending;

    // `save` keeps its identity (a mutation's `mutate`), so the columns are only rebuilt when the copy changes.
    const save = history.save;
    const onRevert = useCallback((nodeId: number, change: m_taxonomy.Change) => {
        if (!tree) return;
        save({
            state: revertChange(tree, nodeId, change, authStore.getUserId()),
            operation: revertOperation(change),
            isRevert: true,
            successMessage: 'Change reverted.',
        });
    }, [tree, save, authStore]);
    const onSaveNote = useCallback((nodeId: number, note: string) => {
        if (!tree) return;
        save({state: setApproverNote(tree, nodeId, note), operation: m_taxonomy.Operation.Review, isRevert: null});
    }, [tree, save]);
    const columns = useMemo(() => getApprovalReviewColumns({
        nodeOf: id => byId.get(id),
        readOnly,
        isSaving,
        canRevert: (nodeId, change) => !!tree && canRevert(tree, nodeId, change),
        onRevert,
        onSaveNote,
    }), [byId, readOnly, isSaving, tree, onRevert, onSaveNote]);

    const backToApprovals = () => navigate(routes.approval);

    return (
        <>
            <Stack gap={2} sx={{p: 2, height: `calc(100% - 64px)`, minHeight: 480}}>
                <ApprovalReviewHeader
                    request={request}
                    onBack={backToApprovals}
                    onUndo={!readOnly && history.canUndo ? history.undo : undefined}
                    onRedo={!readOnly && history.canRedo ? history.redo : undefined}
                />
                {(requestError || stateError) && <Alert severity="error">Could not load the approval request.</Alert>}
                {request?.notes && (
                    <Stack gap={0.5}>
                        <Typography variant="caption" color="text.secondary">Notes from the requester</Typography>
                        <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>{request.notes}</Typography>
                    </Stack>
                )}
                {request?.feedback_notes && readOnly && (
                    <Stack gap={0.5}>
                        <Typography variant="caption" color="text.secondary">Reply</Typography>
                        <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>{request.feedback_notes}</Typography>
                    </Stack>
                )}
                {isLoading
                    // The table's place while the approver's copy loads.
                    ? <Skeleton variant="rounded" sx={{flex: 1, height: 'auto', borderRadius: 1}}/>
                    : (
                        <Stack sx={{flex: 1, minHeight: 0}}>
                            <YourChangesTable
                                rows={rows}
                                tree={tree}
                                isLoading={false}
                                title={`${rows.length} ${rows.length === 1 ? 'changed category' : 'changed categories'}`}
                                extraColumns={columns}
                                layoutKey="taxonomy-builder-v2-approval"
                                noRowsLabel="No changes left to review."
                            />
                        </Stack>
                    )}
            </Stack>
            {!readOnly && (
                <ApprovalReviewActionBar
                    revertedCount={tree ? countReverted(tree) : 0}
                    disabled={!tree || isSaving}
                    onReject={() => setDecision('reject')}
                    onApprove={() => setDecision('approve')}
                />
            )}
            {decision && (
                <FinishReviewDialog
                    decision={decision}
                    initialReply={request?.feedback_notes ?? ''}
                    isSaving={finish.isPending}
                    onCancel={() => setDecision(null)}
                    onConfirm={reply => finish.mutate({decision, feedbackNotes: reply}, {
                        onSuccess: () => {
                            setDecision(null);
                            backToApprovals();
                        },
                    })}
                />
            )}
        </>
    );
});
